/**
 * TanStack Query hooks for user profile with WebSocket invalidation
 * 
 * Profile can be modified by users (self-edit) or admins (verification status),
 * so we use WebSocket events to invalidate the cache when admin changes occur.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { apiClient } from '@/lib/api-client';
import { websocketClient } from '@/lib/websocket-client';
import { useAuth } from '@/contexts/AuthContext';
import { storage } from '@/lib/storage';

export interface Profile {
  id?: string;
  email?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  date_of_birth?: string;
  profile_verification_status?: string;
  identity_verification_status?: string;
  phone_verified?: boolean;
  email_verified?: boolean;
  avatar_url?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  first_purchase_at?: string | null;
  [key: string]: any;
}

export interface ProfileUpdateData {
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  date_of_birth?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

const TEN_MINUTES = 10 * 60 * 1000;

/**
 * Fetch user profile with WebSocket invalidation
 * 
 * This hook:
 * - Caches profile for 10 minutes
 * - Invalidates cache when admin changes verification status (via WebSocket)
 */
export function useProfileQuery() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Subscribe to WebSocket events for cache invalidation
  useEffect(() => {
    if (!user) return;

    const token = storage.getItem('auth_token') || storage.getItem('token');
    if (!token) return;

    // Connect WebSocket (won't reconnect if already connected)
    websocketClient.connect(token).catch(() => {
      // Connection errors handled by websocket client
    });

    // Invalidate profile cache when admin changes verification status
    const handleProfileUpdate = () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    };

    const unsubscribe = websocketClient.subscribe('profile:updated', handleProfileUpdate);

    return () => {
      unsubscribe();
    };
  }, [user, queryClient]);

  return useQuery({
    queryKey: ['profile'],
    queryFn: async (): Promise<Profile | null> => {
      const response = await apiClient.get<{ profile?: Profile }>('/profile');
      
      if (response?.profile) {
        return response.profile;
      }
      
      // Fallback: if response is the profile object directly
      if (response && !response.profile && (response.first_name || response.email)) {
        return response as unknown as Profile;
      }
      
      return null;
    },
    staleTime: TEN_MINUTES,
    gcTime: TEN_MINUTES * 3,
    enabled: !!user,
  });
}

/**
 * Mutation for updating user profile (self-edit)
 * 
 * Automatically invalidates the profile cache on success.
 */
export function useUpdateProfileMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ProfileUpdateData): Promise<Profile> => {
      const response = await apiClient.put<{ profile?: Profile; success?: boolean }>('/profile', data);
      
      if (response?.profile) {
        return response.profile;
      }
      
      // Fallback: if response is the profile object directly
      if (response && (response as any).first_name) {
        return response as unknown as Profile;
      }
      
      throw new Error('Failed to update profile');
    },
    onSuccess: (updatedProfile) => {
      // Update the cache with the new profile data
      queryClient.setQueryData(['profile'], updatedProfile);
      // Also invalidate to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}
